class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', event => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
    });
    this.mobileState = 'closed';
    this.dataset.cartState = 'closed';
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const wishlist = document.querySelector('aveyra-wishlist-drawer');
    if (wishlist && wishlist.state !== 'closed')
      return wishlist.close(false).then(() => this.open(triggeredBy));
    if (!window.matchMedia('(max-width: 749px)').matches) return this.openDesktop(triggeredBy);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (['opening', 'open'].includes(this.mobileState)) {
      if (this.mobileState === 'open') trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
      return;
    }
    const version = this.slideVersion = (this.slideVersion || 0) + 1;
    this.setMobileState('opening');
    if (!this.pageLock) {
      aveyraOverlayScrollLock.acquire(this);
      document.querySelector('header-drawer')?.resetDrawer?.();
      this.pageLock = true;
      document.documentElement.classList.add('aveyra-cart-locked');
      this.pageHideHandler ||= () => this.releasePage();
      window.addEventListener('pagehide', this.pageHideHandler);
      window.addEventListener('resize', this.resizeMobile);
      window.visualViewport?.addEventListener('resize', this.resizeMobile);
      window.visualViewport?.addEventListener('scroll', this.resizeMobile);
      this.addEventListener('touchstart', this.touchStart, { passive: true });
      this.addEventListener('touchmove', this.touchMove, { passive: false });
      this.addEventListener('wheel', this.wheelMove, { passive: false });
    }
    this.resizeMobile();
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    // Lazy drawer images used to start loading only as the panel entered the
    // viewport. Decode the existing images before the first visible frame;
    // all content then travels with the same parent, without image pop-in.
    const images = [...this.querySelectorAll('.cart-item__image')];
    Promise.allSettled(images.map(image => image.decode())).then(() => {
      if (this.slideVersion !== version) return;
      this.classList.add('animate', 'mobile-visible');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.slideVersion !== version) return;
        this.classList.remove('closing');
        this.classList.add('active');
        this.afterSlide(() => {
          if (this.slideVersion !== version || this.mobileState !== 'opening') return;
          this.setMobileState('open');
          trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
        });
      }));
    });
  }

  afterSlide(callback) {
    // Read after the state change has produced transitions; wait for every participant.
    requestAnimationFrame(() => {
      const elements = [this.querySelector('.drawer__inner'), this.querySelector('#CartDrawer-Overlay')];
      Promise.allSettled(elements.flatMap(element => element?.getAnimations() || []).map(animation => animation.finished)).then(callback);
    });
  }

  close() {
    if (!window.matchMedia('(max-width: 749px)').matches && !this.pageLock) return this.closeDesktop();
    if (!this.pageLock || this.mobileState === 'closing') return;
    this.setMobileState('closing');
    const version = this.slideVersion = (this.slideVersion || 0) + 1;
    // Keep active for overlay consumers (newsletter, etc.) until fully closed.
    this.classList.add('closing');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    this.afterSlide(() => {
      // Commit the final transparent/translated frame before changing the
      // document's scroll containment. No duration-based cleanup timer.
      requestAnimationFrame(() => {
        if (this.slideVersion !== version) return;
        this.releasePage();
        removeTrapFocus();
        this.activeElement?.focus({ preventScroll: true });
      });
    });
  }

  releasePage() {
    if (!this.pageLock) return;
    ++this.slideVersion;
    this.pageLock = null;
    document.documentElement.classList.remove('aveyra-cart-locked');
    aveyraOverlayScrollLock.release(this);
    this.classList.remove('active', 'mobile-visible', 'closing');
    this.setMobileState('closed');
    window.removeEventListener('pagehide', this.pageHideHandler);
    window.removeEventListener('resize', this.resizeMobile);
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    window.visualViewport?.removeEventListener('resize', this.resizeMobile);
    window.visualViewport?.removeEventListener('scroll', this.resizeMobile);
    this.removeEventListener('touchstart', this.touchStart);
    this.removeEventListener('touchmove', this.touchMove);
    this.removeEventListener('wheel', this.wheelMove);
    removeTrapFocus();
  }

  setMobileState(state) {
    this.mobileState = state;
    this.dataset.cartState = state;
  }

  wheelMove = event => {
    if (!this.pageLock) return;
    let node = event.target instanceof Element ? event.target : event.target.parentElement;
    while (node && this.contains(node)) {
      if (/auto|scroll/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight + 1 &&
        ((event.deltaY > 0 && node.scrollTop < node.scrollHeight - node.clientHeight - 1) || (event.deltaY < 0 && node.scrollTop > 0))) return;
      node = node.parentElement;
    }
    if (event.cancelable) event.preventDefault();
  };

  resizeMobile = () => {
    if (this.pageLock && !window.matchMedia('(max-width: 749px)').matches) {
      this.releasePage();
      return;
    }
    // Modern Safari owns dynamic viewport sizing in CSS; JS is fallback only.
    if (!CSS.supports('height', '100dvh')) this.style.setProperty('--cart-fallback-height', `${innerHeight}px`);
  };

  touchStart = event => {
    if (event.touches.length === 1) this.touchY = event.touches[0].clientY;
  };

  touchMove = event => {
    if (!this.pageLock || event.touches.length !== 1 || this.touchY === undefined) return;
    const dy = event.touches[0].clientY - this.touchY;
    this.touchY = event.touches[0].clientY;
    let node = event.target instanceof Element ? event.target : event.target.parentElement;
    while (node && this.contains(node)) {
      if (/auto|scroll/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight + 1 &&
        ((dy < 0 && node.scrollTop < node.scrollHeight - node.clientHeight - 1) || (dy > 0 && node.scrollTop > 0))) return;
      node = node.parentElement;
    }
    if (event.cancelable) event.preventDefault();
  };

  openDesktop(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  closeDesktop() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, { open = true } = {}) {
    // The Liquid empty state belongs to the outer custom element, which is
    // preserved when #CartDrawer is replaced. Update it from the same response.
    const incoming = this.getSectionDOM(parsedState.sections?.['cart-drawer'] || '', 'cart-drawer');
    if (!incoming?.querySelector('#CartDrawer')) throw new Error('Cart section unavailable');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector);

      if (sectionElement && sectionHTML !== null) sectionElement.innerHTML = sectionHTML;
    });

    this.classList.toggle('is-empty', incoming.classList.contains('is-empty'));

    if (open) this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;

    const sectionElement = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return sectionElement ? sectionElement.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  updateQuantity(line, quantity, event, name, variantId) {
    const drawer = this.closest('cart-drawer');
    if (drawer?.removalPending) return;
    if (Number(quantity) !== 0) return super.updateQuantity(line, quantity, event, name, variantId);
    return this.removeAnimated(line, drawer);
  }

  async removeAnimated(line, drawer) {
    const row = this.querySelector(`#CartDrawer-Item-${line}`);
    if (!row || !drawer) return;
    drawer.removalPending = true;
    this.setAttribute('aria-busy', 'true');
    const hadFocus = row.contains(document.activeElement);
    const controls = [...drawer.querySelectorAll('button:not(.drawer__close), input, select')].map(e => [e, e.disabled]);
    controls.forEach(([e]) => { e.disabled = true; });
    const panel = drawer.querySelector('.drawer__inner').getBoundingClientRect();
    const header = drawer.querySelector('.drawer__header').getBoundingClientRect();
    const root = drawer.getBoundingClientRect();
    const progress = document.createElement('div');
    progress.className = 'aveyra-cart-removal-progress';
    progress.setAttribute('role', 'progressbar');
    progress.setAttribute('aria-label', 'Aggiornamento del carrello');
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const progressBounds = mobile ? panel : header;
    // Leave a clear 12px gap before desktop table headings.
    Object.assign(progress.style, { left: `${progressBounds.left - root.left}px`,
      top: `${header.bottom - root.top - (mobile ? 0 : 14)}px`, width: `${progressBounds.width}px` });
    progress.append(document.createElement('span'));
    drawer.append(progress);
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const progressPhase = Promise.allSettled(progress.getAnimations({ subtree: true }).map(a => a.finished));
    let confirmed = false;
    let failed = false;
    let originalRowStyle;
    let originalCellStyles;
    try {
      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(), body: JSON.stringify({ id: row.dataset.cartKey, quantity: 0,
          sections: drawer.getSectionsToRender().map(s => s.id), sections_url: location.pathname })
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status) throw new Error(state.description || state.errors || window.cartStrings.error);
      confirmed = true;
      // Never retry the mutation: Shopify can succeed while section rendering fails.
      if (!state.sections?.['cart-drawer'] || !state.sections?.['cart-icon-bubble']) {
        const url = new URL(location.href);
        url.searchParams.set('sections', drawer.getSectionsToRender().map(s => s.id).join(','));
        const sections = await fetch(url, { credentials: 'same-origin' });
        if (sections.ok) state.sections = await sections.json();
      }
      if (!drawer.getSectionDOM(state.sections?.['cart-drawer'] || '', 'cart-drawer #CartDrawer'))
        throw new Error('Prodotto rimosso. Ricarica la pagina per aggiornare il carrello.');
      if (!reduced) await progressPhase;
      progress.classList.add('is-confirmed');
      if (!reduced && row.isConnected) {
        // Measure once, freeze cells in their current positions, then collapse
        // the real row. Following rows reflow continuously, without DOM swaps.
        const rect = row.getBoundingClientRect();
        const style = getComputedStyle(row);
        const frame = { height: `${rect.height}px`, paddingTop: style.paddingTop, paddingBottom: style.paddingBottom,
          marginTop: style.marginTop, marginBottom: style.marginBottom, borderBottomWidth: style.borderBottomWidth,
          opacity: 1, transform: 'translateY(0)' };
        const cells = [...row.children].map(e => [e, e.getBoundingClientRect(), e.getAttribute('style')]);
        originalRowStyle = row.getAttribute('style');
        originalCellStyles = cells;
        cells.forEach(([e, r]) => Object.assign(e.style, { position: 'absolute', left: `${r.left - rect.left}px`,
          top: `${r.top - rect.top}px`, width: `${r.width}px`, height: `${r.height}px` }));
        Object.assign(row.style, { boxSizing: 'border-box', minHeight: '0', overflow: 'hidden', height: `${rect.height}px` });
        const animation = row.animate([frame, { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px',
          marginBottom: '0px', borderBottomWidth: '0px', opacity: 0, transform: 'translateY(-10px)' }],
          { duration: 700, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' });
        await animation.finished.catch(() => {});
      }
      const scrollTop = drawer.querySelector('cart-drawer-items').scrollTop;
      const keepOpen = drawer.classList.contains('active') && !drawer.classList.contains('closing');
      drawer.renderContents(state, { open: false });
      if (!reduced && state.item_count === 0)
        drawer.querySelector('.drawer__inner-empty')?.animate(
          [{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 550, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' });
      const updatedItems = drawer.querySelector('cart-drawer-items');
      updatedItems.scrollTop = scrollTop;
      if (keepOpen) {
        const target = state.item_count === 0 ? drawer.querySelector('.drawer__inner-empty a') :
          drawer.querySelector(`#CartDrawer-Item-${line} .cart-item__name`) || drawer.querySelector('.cart-item__name');
        trapFocus(drawer.querySelector('.drawer__inner'), hadFocus ? target || drawer.querySelector('.drawer__close') : drawer.querySelector('.drawer__close'));
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
    } catch (error) {
      failed = true;
      row.getAnimations().forEach(a => a.cancel());
      if (originalRowStyle !== undefined) originalRowStyle === null ? row.removeAttribute('style') : row.setAttribute('style', originalRowStyle);
      originalCellStyles?.forEach(([e, , value]) => value === null ? e.removeAttribute('style') : e.setAttribute('style', value));
      const errors = drawer.querySelector('#CartDrawer-CartErrors');
      if (errors) errors.textContent = error.message || window.cartStrings.error;
    } finally {
      if (!reduced) await progress.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120 }).finished.catch(() => {});
      progress.remove();
      controls.forEach(([e, disabled]) => { if (e.isConnected) e.disabled = disabled; });
      if (failed && !confirmed && hadFocus && drawer.classList.contains('active'))
        row.querySelector('cart-remove-button button')?.focus({ preventScroll: true });
      this.removeAttribute('aria-busy');
      drawer.removalPending = false;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
